import { useCallback, useContext } from 'react';
import MetricOptionsContext from 'contexts/MetricOptionsContext';
import mergeFilterInputs from 'mergeFilterInputs';
import {
  usePlatesToFilterInput,
  useBuildCompoundMetricInput,
  useBuildMetricInput,
} from 'screens/GoalShow/useGoalData';
import isDefined from 'isDefined';
import EntityDetailsContext from '../../screens/EntityDetailsShow/EntityDetailsContext';

const useBuildKpiInputs = ({
  scorecard,
  selectedQuickFilters,
}: {
  scorecard: Scorecards.Scorecard;
  selectedQuickFilters: LegacySavedFilter[];
}) => {
  const { metricOptions } = useContext(MetricOptionsContext);
  const buildMetricInput = useBuildMetricInput();
  const buildCompoundMetricInput = useBuildCompoundMetricInput();
  const platesToFilterInput = usePlatesToFilterInput();
  const entityDetails = useContext(EntityDetailsContext);

  const buildKpiInputs = useCallback((): KpiInput[] => {
    const quickFilterInputs = selectedQuickFilters.map((qf) =>
      platesToFilterInput(qf.drillDowns),
    );

    const kpis = [...scorecard.kpis] as Scorecards.ScorecardKpi[];
    return kpis
      .map((kpi: Scorecards.ScorecardKpi) => {
        const kpiMetric = metricOptions.find((m) => m.id === kpi.metricId);
        if (!kpiMetric) {
          return undefined;
        }
        const m = buildMetricInput(
          kpi.metricId,
          !!kpi.isDefaultFiltersDisabled,
        );
        const cm = buildCompoundMetricInput(
          kpi.metricId,
          !!kpi.isDefaultFiltersDisabled,
        );
        // mergeFilterInputs takes at most 3 inputs and all 3 slots are used by
        // the KPI's own filters, so the quick filters are folded in via reduce.
        const filters = quickFilterInputs.reduce(
          (acc, quickFilterInput) => mergeFilterInputs(acc, quickFilterInput),
          mergeFilterInputs(
            platesToFilterInput(kpi.drillDowns),
            platesToFilterInput(kpi.scopeDrillDowns),
            kpi.isEntityFiltersDisabled
              ? undefined
              : entityDetails?.entityFilter,
          ),
        );

        return {
          metric: m ? { ...m, id: kpi.id } : undefined,
          compoundMetric: cm ? { ...cm, id: kpi.id } : undefined,
          filters,
          dateField: kpi.queryDateField || 'date',
        };
      })
      .filter(isDefined)
      .filter((kpi) => kpi.metric || kpi.compoundMetric);
  }, [
    buildCompoundMetricInput,
    buildMetricInput,
    entityDetails?.entityFilter,
    metricOptions,
    platesToFilterInput,
    scorecard.kpis,
    selectedQuickFilters,
  ]);

  return buildKpiInputs;
};

export default useBuildKpiInputs;
